import React from 'react';
import { FaTimes, FaCalendarAlt, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';

// Helper to compute next 3 Tuesdays from a base date
export const calculateThreeTuesdays = (baseDateStr, totalAmount) => {
  const baseDate = baseDateStr ? new Date(baseDateStr) : new Date();
  
  // Find the next Tuesday (Tuesday is day 2 in JS: 0=Sun, 1=Mon, 2=Tue)
  const currentDay = baseDate.getDay();
  let daysUntilTuesday = (2 - currentDay + 7) % 7;
  if (daysUntilTuesday === 0) daysUntilTuesday = 7; // If today is Tuesday, first installment next Tuesday or today

  const tuesdays = [];
  const part1 = Math.round((totalAmount / 3) * 100) / 100;
  const part2 = Math.round((totalAmount / 3) * 100) / 100;
  const part3 = Math.round((totalAmount - part1 - part2) * 100) / 100;
  const parts = [part1, part2, part3];

  for (let i = 0; i < 3; i++) {
    const d = new Date(baseDate);
    d.setDate(baseDate.getDate() + daysUntilTuesday + (i * 7));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    tuesdays.push({
      number: i + 1,
      day: `Tuesday ${i + 1}`,
      date: `${yyyy}-${mm}-${dd}`,
      amount: parts[i],
      isPaid: false
    });
  }

  return tuesdays;
};

const TuesdayInstallmentModal = ({ bill, onClose, onToggleInstallmentPaid }) => {
  if (!bill) return null;

  const installments = bill.installments && bill.installments.length === 3
    ? bill.installments
    : calculateThreeTuesdays(bill.dueDate, bill.amount);

  const totalPaid = installments
    .filter(inst => inst.isPaid)
    .reduce((sum, inst) => sum + inst.amount, 0);

  const remaining = Math.max(0, bill.amount - totalPaid);

  return (
    <div className="bt-modal-backdrop" onClick={onClose}>
      <div className="bt-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <div className="bt-modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span className="bt-tuesday-badge">
                <FaCalendarAlt /> 3-Week Tuesday Plan
              </span>
              <span className="bt-badge bt-badge-important">
                <FaShieldAlt /> {bill.priority}
              </span>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.25rem' }}>{bill.description}</h3>
            {bill.subDescription && (
              <div className="bt-subtext">{bill.subDescription}</div>
            )}
          </div>
          <button className="bt-icon-btn" onClick={onClose}>
            <FaTimes size={18} />
          </button>
        </div>

        <div className="bt-modal-body">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 18px',
            backgroundColor: '#f1f5f9',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Total Obligation</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                ${bill.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Remaining Due</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: remaining === 0 ? '#10b981' : '#dc2626' }}>
                ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <p style={{ fontSize: '0.88rem', color: '#475569', marginBottom: '16px' }}>
            High-priority invoices are segmented into 3 weekly tranches payable every <strong>Tuesday</strong> to maintain vendor relations without straining Monday liquidity.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {installments.map((inst, index) => {
              const isPaid = inst.isPaid;
              return (
                <div
                  key={index}
                  className="bt-installment-card"
                  style={{
                    borderColor: isPaid ? '#a7f3d0' : '#e2e8f0',
                    backgroundColor: isPaid ? '#f0fdf4' : '#ffffff'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div
                      className={`bt-checkbox ${isPaid ? 'checked' : ''}`}
                      onClick={() => onToggleInstallmentPaid(bill.id, index)}
                      style={{ width: '22px', height: '22px' }}
                    >
                      {isPaid && <FaCheckCircle size={14} />}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{inst.day}</span>
                        <span style={{ fontSize: '0.82rem', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>
                          {inst.date}
                        </span>
                      </div>
                      <div className="bt-subtext">
                        {isPaid ? 'Paid & cleared from cashflow' : 'Pending payment on scheduled Tuesday'}
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: isPaid ? '#10b981' : '#1e293b' }}>
                      ${inst.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <span style={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: isPaid ? '#059669' : '#d97706',
                      textTransform: 'uppercase'
                    }}>
                      {isPaid ? 'Settled' : 'Scheduled'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bt-modal-footer">
          <button className="bt-btn-secondary" onClick={onClose}>
            Close Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default TuesdayInstallmentModal;
